/**
 * invoicesShared — geteilte Helfer & UI-Primitive für die Rechnungs-/Kunden-
 * Module. Aus InvoicesPage.jsx ausgelagert (Refactoring Jun-2026), damit die
 * Teilkomponenten dieselben Formatierer und Bausteine nutzen.
 */
import { X } from "lucide-react";

export const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export const fmtDate = (iso) => {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    return y && m && d ? `${d}.${m}.${y}` : iso;
};

// WhatsApp-Direktlink (wa.me): DE-Nummer → internationales Format (0… → 49…).
export const waNumber = (phone) => {
    let d = String(phone || "").replace(/[^\d+]/g, "");
    if (!d) return null;
    if (d.startsWith("+")) d = d.slice(1);
    else if (d.startsWith("00")) d = d.slice(2);
    else if (d.startsWith("0")) d = "49" + d.slice(1);
    return d.length >= 8 ? d : null;
};

export const waLink = (c) => {
    const num = waNumber(c.phone);
    if (!num) return null;
    const name = (c.first_name || c.company_name || "").trim();
    return `https://wa.me/${num}?text=${encodeURIComponent(`Hallo ${name}`.trim())}`;
};

export const STATUS_LABEL = {
    draft: { label: "Entwurf", color: "bg-gray-100 text-gray-700" },
    final: { label: "Final", color: "bg-blue-100 text-blue-800" },
    partial_paid: { label: "Teilbezahlt", color: "bg-amber-100 text-amber-800" },
    paid: { label: "Bezahlt", color: "bg-green-100 text-green-800" },
    reminded: { label: "Gemahnt", color: "bg-red-100 text-red-800" },
    cancelled: { label: "Storniert", color: "bg-red-200 text-red-900" },
};

// Wiederverwendbare Seiten-Steuerung: Seitengröße (10/20/50, Standard 20) + Blättern.
export function PaginationBar({ total, page, pageSize, onPage, onPageSize, testid = "pagination" }) {
    const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Pro Seite:</span>
                {[10, 20, 50].map((s) => (
                    <button key={s} type="button" onClick={() => onPageSize(s)}
                        className={`px-2.5 py-1 rounded-sm border font-bold ${pageSize === s ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid={`${testid}-size-${s}`}>
                        {s}
                    </button>
                ))}
                {total > 0 && <span className="text-gray-400 ml-1">{from}–{to} von {Number(total).toLocaleString("de-DE")}</span>}
            </div>
            <div className="flex items-center gap-2 text-xs">
                <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}
                    className="px-2.5 py-1 rounded-sm border border-gray-300 font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#111827]"
                    data-testid={`${testid}-prev`}>Zurück</button>
                <span className="text-gray-600 font-medium" data-testid={`${testid}-info`}>Seite {page} / {pages}</span>
                <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}
                    className="px-2.5 py-1 rounded-sm border border-gray-300 font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#111827]"
                    data-testid={`${testid}-next`}>Weiter</button>
            </div>
        </div>
    );
}

export function ModalShell({ children, onClose, title, wide, testid }) {
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid={testid}>
            <div className={`bg-white rounded-sm w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h2 className="font-display font-black text-lg sm:text-xl">{title}</h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-3 sm:p-5">{children}</div>
            </div>
        </div>
    );
}
